import React, { useState, useRef, useEffect } from 'react';
import { PDF_EXPORT_CLOSE_DROPDOWNS_EVENT } from '../helpers/downloadPageAsPDF';
import '../styles/FilterSelect.scss';

type FilterSelectProps = {
  name: string;
  value: string | string[];
  options: string[];
  noDefault?: boolean;
  multiselect?: boolean;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>, filterName: string) => void;
  onMultiChange?: (values: string[], filterName: string) => void;
};

const limitStringLength = (str: string, length: number) => {
  return str?.length > length ? str?.substring(0, length) + "..." : str;
}

// Compare two string arrays as sets (order-insensitive)
const areSelectionsEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  for (const item of b) {
    if (!setA.has(item)) return false;
  }
  return true;
}

const FilterSelect: React.FC<FilterSelectProps> = ({ 
  name, 
  value, 
  options = [], 
  onChange, 
  onMultiChange,
  noDefault,
  multiselect = false 
}) => {
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  // Initialize temp selected values when dropdown opens (multiselect mode)
  useEffect(() => {
    if (multiselect && showSearch) {
      const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
      setTempSelectedValues(currentValues);
    }
  }, [showSearch, multiselect, value]);

  // Filter options based on search term (case-insensitive)
  useEffect(() => {
    const filtered = options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Apply changes when closing dropdown in multiselect mode
        if (multiselect && onMultiChange && showSearch) {
          const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
          if (!areSelectionsEqual(currentValues, tempSelectedValues)) {
            onMultiChange(tempSelectedValues, name);
          }
        }
        setShowSearch(false);
        setSearchTerm('');
      }
    };

    const closeDropdown = () => {
      setShowSearch(false);
      setSearchTerm('');
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener(PDF_EXPORT_CLOSE_DROPDOWNS_EVENT, closeDropdown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener(PDF_EXPORT_CLOSE_DROPDOWNS_EVENT, closeDropdown);
    };
  }, [multiselect, onMultiChange, showSearch, tempSelectedValues, name]);

  // Focus search input when it appears
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const handleSelectFocus = () => {
    setShowSearch(true);
  };

  const handleSelectClick = () => {
    setShowSearch(true);
  };

  const handleMouseLeave = () => {
    if (multiselect && showSearch) {
      // Apply changes when mouse leaves in multiselect mode
      if (onMultiChange) {
        const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
        if (!areSelectionsEqual(currentValues, tempSelectedValues)) {
          onMultiChange(tempSelectedValues, name);
        }
      }
    }
    setShowSearch(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        if (multiselect) {
          handleOptionClick(filteredOptions[0]);
        } else {
          const currentValue = typeof value === 'string' ? value : '';
          const nextValue = filteredOptions[0];
          if (nextValue !== currentValue) {
            const syntheticEvent = {
              target: { value: nextValue }
            } as React.ChangeEvent<HTMLSelectElement>;
            onChange(syntheticEvent, name);
          }
          setShowSearch(false);
          setSearchTerm('');
        }
      }
    } else if (e.key === 'Escape') {
      // Cancel changes in multiselect mode (reset to original values)
      if (multiselect && showSearch) {
        const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
        setTempSelectedValues(currentValues);
      }
      setShowSearch(false);
      setSearchTerm('');
      selectRef.current?.focus();
    }
  };

  // Handle option click for multiselect
  const handleOptionClick = (option: string) => {
    if (multiselect) {
      // Update temp state only, don't call onMultiChange yet
      setTempSelectedValues(prev => {
        const isSelected = prev.includes(option);
        if (isSelected) {
          return prev.filter(val => val !== option);
        } else {
          return [...prev, option];
        }
      });
    } else {
      // Single select behavior - apply immediately
      const currentValue = typeof value === 'string' ? value : '';
      if (option !== currentValue) {
        const syntheticEvent = {
          target: { value: option }
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent, name);
      }
      setShowSearch(false);
      setSearchTerm('');
    }
  };

  const getDisplayText = () => {
    if (multiselect) {
      const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
      if (currentValues.length === 0) {
        return name.charAt(0).toUpperCase() + name.slice(1);
      }
      if (currentValues.length === 1) {
        return limitStringLength(currentValues[0], 20);
      }
      return `${currentValues.length} selected`;
    }
    return value || (name.charAt(0).toUpperCase() + name.slice(1));
  };

  const isOptionSelected = (option: string) => {
    if (multiselect) {
      // Use temp state when dropdown is open, otherwise use actual value
      if (showSearch) {
        return tempSelectedValues.includes(option);
      }
      const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
      return currentValues.includes(option);
    }
    return value === option;
  };

  return (
    <div className="filter-select-wrapper" ref={containerRef} onMouseLeave={handleMouseLeave}>
      {showSearch ? (
        /* Search input that replaces the select in expanded mode */
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`Search ${name}...`}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          className="filter-select-input"
        />
      ) : (
        /* Original select element with dropdown icon */
        <select
          ref={selectRef}
          onChange={(e) => onChange(e, name)} 
          value={multiselect ? '' : (typeof value === 'string' ? value : '')}
          style={{
            backgroundImage: `url('${__dirname}dropdown.png')`,
          }}
          onFocus={handleSelectFocus}
          onClick={handleSelectClick}
          className="filter-select-element"
          title={String(getDisplayText())}
        >
          <option value="" key="display">{getDisplayText()}</option>
        </select>
      )}

      {/* Dropdown options list when in search mode */}
      {showSearch && (
        <div className="filter-dropdown">
          {noDefault !== true && searchTerm === '' && (
            <div
              onClick={() => {
                if (multiselect) {
                  // Clear temp state and close dropdown
                  setTempSelectedValues([]);
                  if (onMultiChange) {
                    const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
                    if (!areSelectionsEqual(currentValues, [])) {
                      onMultiChange([], name);
                    }
                  }
                } else {
                  const currentValue = typeof value === 'string' ? value : '';
                  if (currentValue !== '') {
                    const syntheticEvent = {
                      target: { value: '' }
                    } as React.ChangeEvent<HTMLSelectElement>;
                    onChange(syntheticEvent, name);
                  }
                }
                setShowSearch(false);
                setSearchTerm('');
              }}
              className={`filter-option ${!multiselect && (typeof value === 'string' && value === '') ? 'selected' : ''}`}
            >
              {name.charAt(0).toUpperCase() + name.slice(1)}
            </div>
          )}
          
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, idx) => (
              <div
                key={`${option}-${idx}`}
                onClick={() => handleOptionClick(option)}
                className={`filter-option ${isOptionSelected(option) ? 'selected' : ''}`}
                title={option}
              >
                {limitStringLength(option, 20)}
              </div>
            ))
          ) : (
            <div className="filter-no-options">
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterSelect;